import React, { useState, useEffect } from 'react';
import './LeaveRequests.css';
import collegeConfig from '../../config/collegeConfig';
import { FaCalendarAlt, FaCheck, FaTimes, FaClock, FaHistory } from 'react-icons/fa';

const LeaveRequests = ({ user, assignedCourse }) => {
  const [activeTab, setActiveTab] = useState('pending');
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewingId, setReviewingId] = useState(null);
  const [reviewNote, setReviewNote] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const token = localStorage.getItem('token');

  useEffect(() => {
    if (assignedCourse) fetchRequests();
  }, [assignedCourse]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const res = await fetch(
        `${collegeConfig.api.baseURL}/leave/for-teacher?courseId=${assignedCourse._id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setRequests(data.requests || []);
      }
    } catch (err) {
      setError('Failed to load leave requests');
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (id, status) => {
    setError('');
    setSuccess('');
    try {
      const res = await fetch(`${collegeConfig.api.baseURL}/leave/${id}/review`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status, reviewNote: reviewNote || undefined })
      });
      const data = await res.json();
      if (data.success) {
        setSuccess(`Request ${status} successfully`);
        setReviewingId(null);
        setReviewNote('');
        fetchRequests();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.message || 'Failed to update request');
      }
    } catch (err) {
      setError('Failed to update request');
    }
  };

  const formatDate = (d) =>
    new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

  const pending = requests.filter(r => r.status === 'pending');
  const history = requests.filter(r => r.status !== 'pending');

  const statusBadge = (status) => {
    const map = {
      pending: { label: 'Pending', color: '#F39C12', icon: <FaClock /> },
      approved: { label: 'Approved', color: '#2ECC71', icon: <FaCheck /> },
      rejected: { label: 'Rejected', color: '#E74C3C', icon: <FaTimes /> }
    };
    const s = map[status] || map.pending;
    return (
      <span className="lr-badge" style={{ background: s.color }}>
        {s.icon}
        {s.label}
      </span>
    );
  };

  const studentName = (req) => {
    const p = req.studentId?.profile;
    if (!p) return 'Unknown Student';
    return `${p.firstName || ''} ${p.lastName || ''}`.trim() || req.studentId?.email || 'Unknown';
  };

  const displayList = activeTab === 'pending' ? pending : history;

  if (!assignedCourse) {
    return (
      <div className="lr-empty-state">
        <p>No course assigned. Please contact admin.</p>
      </div>
    );
  }

  return (
    <div className="lr-container">
      <div className="lr-header">
        <h2><FaCalendarAlt style={{ marginRight: 8 }} />Leave Requests</h2>
        <p className="lr-subtitle">
          Showing requests from your selected students in <strong>{assignedCourse.name}</strong>
        </p>
      </div>

      {error && <div className="lr-alert lr-alert-error">{error}</div>}
      {success && <div className="lr-alert lr-alert-success">{success}</div>}

      <div className="lr-tabs">
        <button
          className={`lr-tab ${activeTab === 'pending' ? 'active' : ''}`}
          onClick={() => setActiveTab('pending')}
        >
          <FaClock style={{ marginRight: 6 }} />
          Pending
          {pending.length > 0 && <span className="lr-tab-count">{pending.length}</span>}
        </button>
        <button
          className={`lr-tab ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          <FaHistory style={{ marginRight: 6 }} />
          History
          {history.length > 0 && <span className="lr-tab-count lr-tab-count-neutral">{history.length}</span>}
        </button>
      </div>

      {loading ? (
        <div className="lr-loading">Loading...</div>
      ) : displayList.length === 0 ? (
        <div className="lr-empty">
          <FaCalendarAlt className="lr-empty-icon" />
          <p>{activeTab === 'pending' ? 'No pending leave requests.' : 'No leave request history.'}</p>
        </div>
      ) : (
        <div className="lr-list">
          {displayList.map((req) => (
            <div key={req._id} className="lr-card">
              <div className="lr-card-top">
                <div className="lr-student-info">
                  <div className="lr-avatar">
                    {studentName(req).split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <div className="lr-student-name">{studentName(req)}</div>
                    {req.studentId?.profile?.registrationNumber && (
                      <div className="lr-reg-no">{req.studentId.profile.registrationNumber}</div>
                    )}
                  </div>
                </div>
                {statusBadge(req.status)}
              </div>

              <div className="lr-dates">
                <FaCalendarAlt className="lr-date-icon" />
                <span>{formatDate(req.dateFrom)}</span>
                <span className="lr-date-sep">—</span>
                <span>{formatDate(req.dateTo)}</span>
              </div>

              {req.reason && (
                <p className="lr-reason"><strong>Reason:</strong> {req.reason}</p>
              )}

              {req.reviewNote && (
                <p className="lr-review-note"><strong>Your note:</strong> {req.reviewNote}</p>
              )}

              <p className="lr-submitted-on">Submitted {formatDate(req.createdAt)}</p>

              {req.status === 'pending' && (
                <div className="lr-actions">
                  {reviewingId === req._id ? (
                    <div className="lr-review-panel">
                      <textarea
                        className="lr-note-input"
                        placeholder="Optional note to student..."
                        value={reviewNote}
                        onChange={(e) => setReviewNote(e.target.value)}
                        rows={2}
                      />
                      <div className="lr-review-btns">
                        <button
                          className="lr-btn lr-btn-approve"
                          onClick={() => handleReview(req._id, 'approved')}
                        >
                          <FaCheck /> Approve
                        </button>
                        <button
                          className="lr-btn lr-btn-reject"
                          onClick={() => handleReview(req._id, 'rejected')}
                        >
                          <FaTimes /> Reject
                        </button>
                        <button
                          className="lr-btn lr-btn-cancel"
                          onClick={() => { setReviewingId(null); setReviewNote(''); }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      className="lr-btn lr-btn-review"
                      onClick={() => { setReviewingId(req._id); setReviewNote(''); }}
                    >
                      Review
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LeaveRequests;
